import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { UtilService } from '../../../../services/util.service';
import { API, VisitorTrackerDTO } from '../../../../services/api/api';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../../services/error.service';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { RequestClass } from '../../../../services/global/RequestClass';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButton } from '../../../../services/global/global-interfaces';
import { RoutingID } from '../../../../routes/sb-routing-enum';

type SplitButtonAction = 'details';

@Component({
  selector: 'sb-page-location-tracker',
  templateUrl: './location-tracker.component.html',
  styleUrls: ['./location-tracker.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationTrackerComponent extends RequestClass {
  public readonly errorHandler = inject(ErrorService);
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @Input() locationId: number = undefined;

  // table wrapper variables
  trackers: VisitorTrackerDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.locationId || !this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .dm_visitor_VisitorTracker_retrieveAll(
        {
          location_id: this.locationId,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: data => {
          this.trackers = this.buildSplitButtons(data);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  getLocationId() {
    const arr = [];
    if (this.locationId) {
      arr.push(this.locationId);
      return JSON.stringify(arr);
    }
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      item.sbButtons.push({
        icon: 'details',
        tooltip: 'lang.globals.openDetails',
        link: RoutingID.trackerDetails,
        pathParams: [{ id: 'trackerID', value: item?.id }],
      });
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
